import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as marketApi from '../api/marketplace';
import { useToast } from '../components/toast';
import { Spinner, ErrorAlert, EmptyState, Field, TextareaField } from '../components/ui';
import {
  PURCHASE_ANSWERS,
  PURCHASE_LABELS,
  REVIEW_TEXT_MAX,
  amountToCents,
} from '../lib/marketplace';
import { fieldErrorsFrom } from '../lib/formErrors';
import { cn, ui } from '../lib/ui';

/**
 * /marketplace/follow-up/:leadId: the member's follow-up (docs/Decisions.md
 * D68 item 8; docs/MarketplaceDesign.md section 3.6). Every question is
 * optional except whether they bought. Only a buyer can review (CR-35), and
 * nothing is offered for a review.
 */
export default function MarketplaceFollowUp() {
  const { leadId } = useParams();
  const toast = useToast();
  const [lead, setLead] = useState(undefined);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    purchased: '',
    price: '',
    rating: 0,
    text: '',
    recommend: false,
  });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    marketApi
      .listMyLeads()
      .then(d => setLead(d.leads.find(l => l.leadId === leadId) || null))
      .catch(setError);
  }, [leadId]);

  const set = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: undefined }));
  };

  async function submit(e) {
    e.preventDefault();
    const found = {};
    if (!form.purchased) found.purchased = 'Say whether you bought the service';
    const pricePaidCents =
      form.purchased === 'yes' && form.price.trim() ? amountToCents(form.price) : undefined;
    if (Number.isNaN(pricePaidCents))
      found.pricePaidCents = 'Enter an amount such as 1200 or 1200.00';
    if (Object.keys(found).length) {
      setErrors(found);
      return;
    }
    setSending(true);
    try {
      await marketApi.answerFollowUp(leadId, {
        purchased: form.purchased,
        pricePaidCents,
        review:
          form.purchased === 'yes' && form.rating
            ? { rating: form.rating, text: form.text.trim() }
            : undefined,
        recommend: form.recommend || undefined,
      });
      toast.success('Thank you for telling us');
      setDone(true);
    } catch (err) {
      setErrors(fieldErrorsFrom(err));
      toast.error(err.message || 'Your answers were not saved');
    } finally {
      setSending(false);
    }
  }

  const title = <h1 className={ui.h1}>How did it go?</h1>;
  if (error) {
    return (
      <>
        {title}
        <ErrorAlert error={error} />
      </>
    );
  }
  if (lead === undefined) return <Spinner label="Loading..." />;
  if (lead === null) {
    return (
      <>
        {title}
        <EmptyState message="This follow-up is not yours, or it no longer exists." />
      </>
    );
  }
  if (done) {
    return (
      <>
        {title}
        <p className={ui.alertSuccess}>
          Thank you. Your answers help {lead.vendorName} and the organization keep the marketplace
          trustworthy.
        </p>
        <Link to={`/marketplace/vendors/${encodeURIComponent(lead.vendorId)}`}>
          Back to {lead.vendorName}
        </Link>
      </>
    );
  }

  return (
    <>
      {title}
      <p className={cn(ui.muted, 'mb-4')}>
        You joined {lead.vendorName}&apos;s interest list for {lead.serviceName}. Every question but
        the first is optional.
      </p>
      <form onSubmit={submit} className={cn(ui.card, 'max-w-prose')} noValidate>
        <fieldset className="mb-4">
          <legend className={ui.label}>Did you buy this service?</legend>
          <div className={ui.row}>
            {PURCHASE_ANSWERS.map(a => (
              <label key={a} className="flex items-center gap-2 text-body-s">
                <input
                  type="radio"
                  name="purchased"
                  value={a}
                  checked={form.purchased === a}
                  onChange={() => set('purchased', a)}
                />
                {PURCHASE_LABELS[a]}
              </label>
            ))}
          </div>
          {errors.purchased && <p className={ui.fieldError}>{errors.purchased}</p>}
        </fieldset>
        {form.purchased === 'yes' && (
          <>
            <Field
              label="About what did you pay? (USD, optional)"
              inputMode="decimal"
              value={form.price}
              onChange={e => set('price', e.target.value)}
              error={errors.pricePaidCents}
            />
            <fieldset className="mb-4">
              <legend className={ui.label}>Leave a review (optional)</legend>
              <div className={ui.row}>
                {[1, 2, 3, 4, 5].map(n => (
                  <label key={n} className="flex items-center gap-1 text-body-s">
                    <input
                      type="radio"
                      name="rating"
                      value={n}
                      checked={form.rating === n}
                      onChange={() => set('rating', n)}
                    />
                    {n} star{n === 1 ? '' : 's'}
                  </label>
                ))}
              </div>
              {errors['review.rating'] && (
                <p className={ui.fieldError}>{errors['review.rating']}</p>
              )}
            </fieldset>
            {form.rating > 0 && (
              <TextareaField
                label="Your review"
                rows={4}
                maxLength={REVIEW_TEXT_MAX}
                value={form.text}
                onChange={e => set('text', e.target.value)}
                error={errors['review.text']}
                hint="Shown with your name as a verified customer. Nothing is offered for reviews."
              />
            )}
          </>
        )}
        <label className="mb-4 flex items-start gap-2 text-body-s">
          <input
            type="checkbox"
            className={cn(ui.checkbox, 'mt-1')}
            checked={form.recommend}
            onChange={e => set('recommend', e.target.checked)}
          />
          Recommend {lead.vendorName} on my profile
        </label>
        <button type="submit" className={ui.btnPrimary} disabled={sending}>
          {sending ? 'Sending...' : 'Send'}
        </button>
      </form>
    </>
  );
}
